// Pure helpers for the multi-tank Overview home — the grid view a
// keeper with two or more tanks lands on instead of the single-tank
// Home. The job is "at-a-glance": one card per tank, three pieces of
// info — the Sensei Score with its band color, what's worst right now,
// and how fresh the data is. Anything deeper lives on the per-tank
// Home (a tap away).
//
// Pure on purpose: same shape as forecast.js / dose-calc.js. The grid
// + the unit tests both read from this one module so a tweak to the
// "worst off-band" rule or a band-color change can't desync.

import { scoreTier } from '../../shared/score-band.js';

// Score → band/tone. SenseiScore.jsx uses this too, so the big number
// in the overview card matches the big number in the tank header. The
// cut-offs come from shared/score-band.js, the same rule /next and the
// displays use: a 78 must not read "watch" here and "good" on a View 7.
export function scoreTone(s) {
  const tier = s == null ? null : scoreTier(s);
  if (!tier) return { band: 'unknown', color: 'var(--text-dim)', label: 'pending' };
  if (tier === 'excellent' || tier === 'steady') {
    return { band: 'good', color: 'var(--ok, #6cc18a)', label: 'good' };
  }
  if (tier === 'watch') return { band: 'watch', color: 'var(--warn, #d6a754)', label: 'watch' };
  return { band: 'attention', color: 'var(--alert, #d97070)', label: 'attention' };
}

// Status-pill label for a tank's score — "Good" / "Watch" /
// "Attention" / "—". The grid card shows this as a coloured pill next
// to the tank name (band color from scoreTone).
export function bandPill(s) {
  const { band, color } = scoreTone(s);
  if (band === 'good') return { label: 'Good', band, color };
  if (band === 'watch') return { label: 'Watch', band, color };
  if (band === 'attention') return { label: 'Attention', band, color };
  return { label: '—', band: 'unknown', color };
}

// How many tests the score graded (a number on the entry). Logged-only
// readings (a fishless cycle's ammonia, a QT's salinity) and stale ones
// carry none.
export function gradedCount(breakdown) {
  if (!breakdown || typeof breakdown !== 'object') return 0;
  return Object.entries(breakdown).filter(([k, e]) => k !== '_meta' && typeof e?.score === 'number')
    .length;
}

// The score is held at its coverage cap with every graded test inside its
// range: the number says how few tests are logged, not that the tank needs
// attention. One in-range temperature read "40 · Needs attention" with a
// red pill (newkeeper audit 2026-10-01). Reads the score response's
// coverage fields, or the list row's breakdown._meta.
export function isCoverageCapped(s) {
  if (!s || typeof s.health_score !== 'number') return false;
  const meta = s.parameter_breakdown?._meta || {};
  const coverage = typeof s.coverage === 'number' ? s.coverage : meta.coverage;
  const cap = typeof s.coverage_cap === 'number' ? s.coverage_cap : meta.coverage_cap;
  if (typeof coverage !== 'number' || typeof cap !== 'number' || coverage >= 1) return false;
  if (s.health_score < cap) return false;
  const bd = s.parameter_breakdown || {};
  return !Object.entries(bd).some(
    ([k, e]) => k !== '_meta' && typeof e?.score === 'number' && e.score < 100
  );
}

const PARTIAL = { band: 'partial', color: 'var(--text-muted)', label: 'partial' };

// A score row with readings but nothing graded (every entry logged-only or
// stale, or alert-only at weight 0, like a reef tank's ammonia), or with no
// entries at all and the stored 0. The tank's own card shows the empty
// state for the same rows (server/score.js buildScoreResponse).
function isUngraded(tank) {
  const bd = tank?.parameter_breakdown;
  if (!bd || typeof bd !== 'object') return false;
  const entries = Object.keys(bd).filter((k) => k !== '_meta');
  if (entries.length) {
    return !entries.some((k) => typeof bd[k]?.score === 'number' && Number(bd[k].weight ?? 1) > 0);
  }
  return !!bd._meta && tank.health_score === 0;
}

// The tone for a tank's big number: none when nothing is graded (the
// stored 0 is not a score), neutral when the coverage cap is all it says.
export function tankTone(tank) {
  if (isUngraded(tank)) return scoreTone(null);
  if (isCoverageCapped(tank)) return PARTIAL;
  return scoreTone(tank?.health_score);
}

// The score to show, or null when nothing was graded.
export function shownScore(tank) {
  if (typeof tank?.health_score !== 'number') return null;
  if (isUngraded(tank)) return null;
  return tank.health_score;
}

// The card's pill for a whole tank. A score is a weighted average, so a
// 92 can hold a temperature and a nitrate that are both off the keeper's
// range; "Good" beside those read as all clear (audit 2026-10-01). A tank
// with any parameter outside its range is at least "Watch". A score held
// down only by how few tests are logged reads "Partial", never
// "Attention", and a coverage-capped tank with a parameter off reads
// "Watch": the low number is the cap, not the reading.
export function tankPill(tank) {
  if (shownScore(tank) == null) {
    // No number, but an alert-only test out of its range (a fish-in cycle's
    // ammonia on a reef tank, weight 0) still needs a look: the header said
    // "Nothing off" over an ammonia of 1.0 (confirm pass of sweep 4).
    if (hasOffBandParam(tank?.parameter_breakdown)) {
      const { color } = scoreTone(70);
      return { label: 'Watch', band: 'watch', color };
    }
    return bandPill(null);
  }
  if (isCoverageCapped(tank)) return { ...PARTIAL, label: 'Partial' };
  const pill = bandPill(tank?.health_score);
  const offBand = hasOffBandParam(tank?.parameter_breakdown);
  if (pill.band === 'good' && offBand) {
    const { color } = scoreTone(70);
    return { label: 'Watch', band: 'watch', color };
  }
  if (pill.band === 'attention' && coverageOf(tank) < 1 && tank.health_score >= capOf(tank)) {
    const { color } = scoreTone(70);
    return { label: 'Watch', band: 'watch', color };
  }
  return pill;
}

function coverageOf(tank) {
  const c = tank?.coverage ?? tank?.parameter_breakdown?._meta?.coverage;
  return typeof c === 'number' ? c : 1;
}
function capOf(tank) {
  const c = tank?.coverage_cap ?? tank?.parameter_breakdown?._meta?.coverage_cap;
  return typeof c === 'number' ? c : Infinity;
}

// Tally tanks by band for the header summary ("3 GOOD · 1 WATCH"), the
// same band each card's pill shows. Tanks without a score yet count as
// 'unknown' and are omitted from the headline counts (they read as
// "pending" on their own card).
export function bandCounts(tanks) {
  const counts = { good: 0, watch: 0, attention: 0, partial: 0, unknown: 0 };
  for (const t of Array.isArray(tanks) ? tanks : []) {
    const { band } = tankPill(t);
    counts[band] = (counts[band] || 0) + 1;
  }
  return counts;
}

// Friendly param labels — re-exported here so existing callers
// (test/tank-overview.test.js, TankOverviewGrid) keep their import path.
export { paramLabel } from '../../shared/param-labels.js';
import { paramLabel } from '../../shared/param-labels.js';

// "low" | "high" | "off" — same vocabulary the device downlink + the
// score breakdown emit. Prefers the keeper-facing acceptable band when
// the breakdown carries it (newer rows do — see score.js breakdown
// builder); falls back to the target band for legacy rows. Without this
// preference, a value sitting inside [target_low, target_high] but
// outside the narrower [acceptable_low, acceptable_high] would score
// <100 (the curve runs from the acceptable edge) but report status
// 'off' instead of 'low'/'high' — a false-direction headline on the
// Overview card.
export function statusFromEntry(entry) {
  if (!entry || entry.track_only || typeof entry.score !== 'number') return null;
  if (entry.score >= 100) return 'ok';
  const { value } = entry;
  const lo = typeof entry.acceptable_low === 'number' ? entry.acceptable_low : entry.target_low;
  const hi = typeof entry.acceptable_high === 'number' ? entry.acceptable_high : entry.target_high;
  if (typeof value === 'number') {
    if (typeof lo === 'number' && value < lo) return 'low';
    if (typeof hi === 'number' && value > hi) return 'high';
  }
  return 'off';
}

// True when a graded parameter sits outside the keeper's range (LOW or
// HIGH). A value that only lost score points inside the range is not.
export function hasOffBandParam(breakdown) {
  if (!breakdown || typeof breakdown !== 'object') return false;
  return Object.entries(breakdown).some(([k, e]) => {
    if (k === '_meta' || e?.stale) return false;
    const st = statusFromEntry(e);
    return st === 'low' || st === 'high';
  });
}

// Pick the parameter most worth showing on the card. Strategy:
//   1. Off-band wins over in-range — the card is about "what should the
//      reefer look at first."
//   2. Among off-band, the WORST normalised distance past the band edge
//      wins. Normalising by the target keeps a small alkalinity miss
//      from being out-shouted by a nitrate that's 5 ppm "off" of a
//      5 ppm target (i.e. 100% off — which IS more dramatic).
//   3. Tie / no off-band → null. The caller renders "all in range".
//
// Returns { key, label, status: 'low' | 'high' | 'off', value, target }
// or null.
export function worstOffParam(breakdown) {
  if (!breakdown || typeof breakdown !== 'object') return null;
  let worst = null;
  let worstNorm = -Infinity;
  for (const [key, entry] of Object.entries(breakdown)) {
    if (key === '_meta') continue;
    const status = statusFromEntry(entry);
    if (!status || status === 'ok') continue;
    const { value } = entry;
    const lo = typeof entry.acceptable_low === 'number' ? entry.acceptable_low : entry.target_low;
    const hi =
      typeof entry.acceptable_high === 'number' ? entry.acceptable_high : entry.target_high;
    if (typeof value !== 'number') continue;
    let target;
    let distance;
    if (status === 'low' && typeof lo === 'number') {
      target = lo;
      distance = lo - value;
    } else if (status === 'high' && typeof hi === 'number') {
      target = hi;
      distance = value - hi;
    } else {
      // 'off' with ambiguous direction — count as a fixed mild signal.
      target = typeof lo === 'number' ? lo : typeof hi === 'number' ? hi : null;
      distance = 0;
    }
    const denom = target && Math.abs(target) > 0 ? Math.abs(target) : 1;
    const norm = distance / denom;
    if (norm > worstNorm) {
      worstNorm = norm;
      worst = { key, label: paramLabel(key), status, value, target };
    }
  }
  return worst;
}

// One calm sentence under the score. "Steady on every parameter."
// when the tank is in range; "Alkalinity is low" when one param is
// off; "Calcium and 2 others need attention" when multiple are off.
// Honest about empty state: if there's no score / no breakdown yet
// the card says so instead of pretending.
export function headlineFor({ healthScore, breakdown }) {
  if (healthScore == null) return 'Waiting for the first reading.';
  const bd = breakdown && typeof breakdown === 'object' ? breakdown : {};
  // Stale params have score:null (dropped from the weighted average) so
  // they're invisible to the off-band logic below — but a stuck probe
  // is exactly what the keeper needs to hear about. Surface it FIRST so
  // the card never reads "All parameters in range" while a reading is
  // frozen / stuck / silent.
  const stale = Object.entries(bd).filter(([k, e]) => k !== '_meta' && e && e.stale);
  const params = Object.entries(bd).filter(
    ([k, e]) => k !== '_meta' && e && typeof e.score === 'number'
  );
  const off = params.filter(([, e]) => e.score < 100);
  if (off.length === 0 && stale.length > 0) {
    const label = paramLabel(stale[0][0]);
    return stale.length === 1
      ? `${label} reading has gone stale — log a fresh test.`
      : `${stale.length} readings have gone stale — check your probes.`;
  }
  if (params.length === 0 && stale.length === 0) return 'Waiting for the first reading.';
  if (off.length === 0) {
    return stale.length > 0
      ? `In range, but ${stale.length} reading${stale.length > 1 ? 's' : ''} stale.`
      : 'All parameters in range.';
  }
  const worst = worstOffParam(bd);
  const staleTail = stale.length > 0 ? ` · ${stale.length} stale` : '';
  if (!worst) {
    return off.length === 1
      ? `1 parameter needs attention.${staleTail}`
      : `${off.length} parameters need attention.${staleTail}`;
  }
  const dir = worst.status === 'low' ? 'low' : worst.status === 'high' ? 'high' : 'off';
  if (off.length === 1) return `${worst.label} is ${dir}.${staleTail}`;
  const others = off.length - 1;
  return `${worst.label} is ${dir} · ${others} other${others > 1 ? 's' : ''} off too.${staleTail}`;
}

// Header summary line — names the tank that most needs a look. Picks
// the lowest-scoring tank that isn't 'good', and quotes its worst
// off-band param ("Quarantine needs a look — nitrate is high"). When
// every tank is good (or there's nothing to score yet), reassures
// instead of inventing a concern.
export function summaryHeadline(tanks) {
  const list = (Array.isArray(tanks) ? tanks : []).filter(
    (t) => typeof t?.health_score === 'number'
  );
  if (list.length === 0) return 'Waiting for your first readings.';
  const offBand = list
    .filter((t) => ['watch', 'attention'].includes(tankPill(t).band))
    .sort((a, b) => a.health_score - b.health_score);
  if (offBand.length === 0) {
    return list.every((t) => tankPill(t).band === 'good')
      ? 'Every tank is looking good.'
      : 'Nothing off in what you have logged so far.';
  }
  const worst = offBand[0];
  const wp = worstOffParam(worst.parameter_breakdown);
  if (!wp) return `${worst.name} needs a look.`;
  const dir = wp.status === 'low' ? 'low' : wp.status === 'high' ? 'high' : 'off';
  return `${worst.name} needs a look — ${wp.label.toLowerCase()} is ${dir}.`;
}

// "just now" / "12 min ago" / "2 h ago" / "3 days ago" — same shape as
// the freshness stamp on the score card. null timestamp → "no readings
// yet". Sub-minute reads as "just now" — no other surface in the app
// shows seconds, and a probe-fed reading landing mid-heartbeat would
// otherwise read "3s ago" / "0s ago" right after the user opened the
// page.
export function freshnessLabel(ts, now = Date.now()) {
  if (!ts) return 'no readings yet';
  const t = ts instanceof Date ? ts.getTime() : new Date(ts).getTime();
  if (!Number.isFinite(t)) return 'no readings yet';
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  const d = Math.floor(s / 86400);
  if (d === 1) return '1 day ago';
  if (d < 14) return `${d} days ago`;
  if (d < 60) return `${Math.floor(d / 7)} weeks ago`;
  return `${Math.floor(d / 30)} months ago`;
}

// Compact tank-age label from the established date — "new", "3 weeks",
// "8 mo", "2y". Null / unparseable → null (the caller omits it).
export function tankAgeLabel(establishedDate, now = Date.now()) {
  if (!establishedDate) return null;
  const t =
    establishedDate instanceof Date
      ? establishedDate.getTime()
      : new Date(establishedDate).getTime();
  if (!Number.isFinite(t)) return null;
  const days = Math.floor((now - t) / 86_400_000);
  if (days < 0) return null;
  if (days < 14) return 'new';
  if (days < 60) return `${Math.floor(days / 7)} weeks`;
  if (days < 365) return `${Math.floor(days / 30)} mo`;
  const years = days / 365;
  const rounded = Math.round(years * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}y`;
}

// Phase → display label. Mirrors the five-phase lifecycle
// (setup / cycling / maturing / growing / established). Unknown / null
// → null so the caller omits it rather than showing "Unknown".
const PHASE_LABEL = {
  setup: 'Setup',
  cycling: 'Cycling',
  maturing: 'Maturing',
  growing: 'Growing',
  established: 'Established',
};
export function phaseLabel(phase) {
  return PHASE_LABEL[phase] || null;
}

// Compact livestock summary — "4 fish · 18 coral · 2 inverts" omitting
// zero kinds. Null / empty → null. Singular/plural handled per kind
// (fish is invariant; coral stays "coral"; invert → inverts).
export function livestockSummary(livestock) {
  if (!livestock || typeof livestock !== 'object') return null;
  const bits = [];
  if (livestock.fish > 0) bits.push(`${livestock.fish} fish`);
  if (livestock.coral > 0) bits.push(`${livestock.coral} coral`);
  if (livestock.invert > 0)
    bits.push(`${livestock.invert} invert${livestock.invert > 1 ? 's' : ''}`);
  return bits.length ? bits.join(' · ') : null;
}

// Dosing chip label — "Dosing alk" with the friendly-ish param. Null
// dosing → null. The rate is in a tooltip, not the chip, to keep it
// glanceable.
const DOSE_PARAM_SHORT = { alk: 'alk', calcium: 'Ca', magnesium: 'Mg' };
export function dosingLabel(dosing) {
  if (!dosing || !dosing.param) return null;
  const p = DOSE_PARAM_SHORT[dosing.param] || dosing.param;
  return `Dosing ${p}`;
}
